import { useEffect, useMemo, useState, type ReactElement } from "react";
import { LayoutGrid, Plus, Search, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, EmptyState, Select, Stack, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, ConfirmAction, SplitPane, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { useApiClient } from "@shell/apiClientContext";
import { useSageOffices } from "../sage-offices/hooks";
import { BulkActionBar } from "../common/BulkActionBar";
import { describeApiError } from "../common/errors";
import { FLOORPLAN_QUERY_KEY } from "../common/queryKeys";
import { runBulk, summarizeBulkFailures } from "../common/runBulk";
import { ExportDataButton } from "../common/ExportDataButton";
import { buildWorkspacesExportCsv } from "../common/exportCsv";
import { AssignTargetDialog } from "../assets/AssignTargetDialog";
import { AssetForm, type AssetFormWorkspaceContext } from "../assets/AssetForm";
import { AssetPickerDialog } from "../assets/AssetPickerDialog";
import type { AssetUpsertPayload, AssetView } from "../assets/types";
import {
    ASSET_ASSIGNMENTS_QUERY_KEY,
    useCreateAssetAssignment,
    useReleaseAssetAssignment,
} from "../asset-assignments/hooks";
import type { AssetAssignmentView } from "../asset-assignments/types";
import {
    WORKSPACE_ASSIGNMENTS_QUERY_KEY,
    useCreateWorkspaceAssignment,
    useReleaseWorkspaceAssignment,
} from "../workspace-assignments/hooks";
import {
    createWorkspaceAssignment,
    releaseWorkspaceAssignment,
} from "../workspace-assignments/api";
import { WorkspacesList } from "./WorkspacesList";
import { WorkspaceForm, type WorkspaceFormMode } from "./WorkspaceForm";
import { ReassignAllAssetsDialog } from "./ReassignAllAssetsDialog";
import { workspaceTypes, type WorkspaceUpsertPayload, type WorkspaceView } from "./types";
import {
    ASSETS_QUERY_KEY,
} from "../assets/hooks";
import {
    WORKSPACES_QUERY_KEY,
    useAddAssetToWorkspace,
    useCreateWorkspace,
    useDeleteWorkspace,
    useRestoreWorkspace,
    useUpdateWorkspace,
    useWorkspaces,
} from "./hooks";
import { deleteWorkspace, exportWorkspaces } from "./api";

/**
 * Page modes for the workspaces admin surface. Most of the kinds map 1:1
 * to a screen on the right pane; `add-asset` is the campaign-workflow
 * sub-mode where the right pane temporarily belongs to the asset form,
 * scoped to a specific workspace.
 *
 * The "assign existing" picker is intentionally *not* a mode — it's a
 * dialog that overlays the `edit` screen, so dismissing it returns the
 * user exactly to where they were without any back-and-forth navigation.
 */
type Mode =
    | { kind: "idle" }
    | { kind: "create" }
    | { kind: "edit"; id: string }
    | { kind: "add-asset"; workspaceId: string };

export function WorkspacesPage(): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const navigate = useNavigate();
    const client = useApiClient();
    const [search, setSearch] = useState("");
    const [officeFilter, setOfficeFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [mode, setMode] = useState<Mode>({ kind: "idle" });
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);
    const [pendingRestore, setPendingRestore] = useState<string | null>(null);
    const [pendingRelease, setPendingRelease] = useState<AssetAssignmentView | null>(null);
    const [assignExistingFor, setAssignExistingFor] = useState<string | null>(null);
    const [reassignAllFor, setReassignAllFor] = useState<string | null>(null);
    const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(() => new Set());
    const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
    const [bulkIntent, setBulkIntent] = useState<null | "delete" | "available">(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkError, setBulkError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const officesQuery = useSageOffices({ q: "", includeDeleted: true });
    const offices = useMemo(() => officesQuery.data ?? [], [officesQuery.data]);
    const officeIndex = useMemo(() => {
        const map = new Map<string, (typeof offices)[number]>();
        for (const o of offices) map.set(o.id, o);
        return map;
    }, [offices]);

    const workspacesQuery = useWorkspaces({
        q: search.trim(),
        sageOfficeId: officeFilter,
        type: typeFilter,
        includeDeleted,
    });

    const createMutation = useCreateWorkspace();
    const updateMutation = useUpdateWorkspace();
    const deleteMutation = useDeleteWorkspace();
    const restoreMutation = useRestoreWorkspace();
    const addAssetMutation = useAddAssetToWorkspace();
    const createAssignmentMutation = useCreateAssetAssignment();
    const releaseAssignmentMutation = useReleaseAssetAssignment();
    // Occupant management on the workspace edit form. The create call is
    // atomic — if the workspace already has an occupant, the backend
    // releases them in the same Cosmos batch as the new assignment.
    // That's why a single "assign" mutation covers both the vacant and
    // re-assign branches with no orchestration here.
    const createOccupantMutation = useCreateWorkspaceAssignment();
    const releaseOccupantMutation = useReleaseWorkspaceAssignment();

    const rows = useMemo(() => workspacesQuery.data ?? [], [workspacesQuery.data]);

    useEffect(() => {
        setSelectedKeys((prev) => {
            if (prev.size === 0) return prev;
            const ids = new Set(rows.map((row) => row.id));
            let changed = false;
            const next = new Set<string>();
            for (const id of prev) {
                if (ids.has(id)) next.add(id);
                else changed = true;
            }
            return changed ? next : prev;
        });
    }, [rows]);

    const selectedWorkspaces = useMemo(
        () => rows.filter((row) => selectedKeys.has(row.id) && !row.isDeleted),
        [rows, selectedKeys],
    );
    const workspaceIndex = useMemo(() => {
        const map = new Map<string, (typeof rows)[number]>();
        for (const w of rows) map.set(w.id, w);
        return map;
    }, [rows]);

    const selectedRecord = useMemo(() => {
        if (mode.kind !== "edit") return null;
        return workspaceIndex.get(mode.id) ?? null;
    }, [mode, workspaceIndex]);

    const formMode: WorkspaceFormMode | null = useMemo(() => {
        if (mode.kind === "create")
            return {
                kind: "create",
                defaultSageOfficeId: officeFilter !== "" ? officeFilter : null,
            };
        if (mode.kind === "edit" && selectedRecord !== null)
            return { kind: "edit", record: selectedRecord };
        return null;
    }, [mode, selectedRecord, officeFilter]);

    // The workspace currently driving the asset-form / picker contexts.
    // Pulled from the master list so deletes/edits flow through.
    const targetWorkspace = useMemo(() => {
        if (mode.kind === "add-asset") return workspaceIndex.get(mode.workspaceId) ?? null;
        if (assignExistingFor !== null) return workspaceIndex.get(assignExistingFor) ?? null;
        return null;
    }, [mode, assignExistingFor, workspaceIndex]);

    const reassignAllSource =
        reassignAllFor !== null ? (workspaceIndex.get(reassignAllFor) ?? null) : null;

    const assetFormWorkspaceContext: AssetFormWorkspaceContext | undefined = useMemo(() => {
        if (mode.kind !== "add-asset" || targetWorkspace === null) return undefined;
        return {
            id: targetWorkspace.id,
            name: targetWorkspace.name,
            type: targetWorkspace.type,
        };
    }, [mode, targetWorkspace]);

    const isSaving = createMutation.isPending || updateMutation.isPending;
    const isAssetSaving =
        addAssetMutation.isPending ||
        createAssignmentMutation.isPending ||
        releaseAssignmentMutation.isPending;
    const formError =
        mode.kind === "create" && createMutation.isError
            ? describeApiError(createMutation.error)
            : mode.kind === "edit" && updateMutation.isError
              ? describeApiError(updateMutation.error)
              : null;
    const assetFormError =
        mode.kind === "add-asset" && addAssetMutation.isError
            ? describeApiError(addAssetMutation.error)
            : null;

    const isOccupantBusy = createOccupantMutation.isPending || releaseOccupantMutation.isPending;
    const occupantErrorMessage = createOccupantMutation.isError
        ? describeApiError(createOccupantMutation.error)
        : releaseOccupantMutation.isError
          ? describeApiError(releaseOccupantMutation.error)
          : null;

    function handleAssignOccupant(userId: string): void {
        if (mode.kind !== "edit") return;
        const workspaceId = mode.id;
        createOccupantMutation.mutate({
            id: crypto.randomUUID(),
            workspaceId,
            userId,
        });
    }

    function handleReleaseOccupant(currentAssignmentId: string): void {
        if (mode.kind !== "edit") return;
        const workspaceId = mode.id;
        releaseOccupantMutation.mutate({
            workspaceId,
            assignmentId: currentAssignmentId,
        });
    }

    function handleSave(payload: WorkspaceUpsertPayload): void {
        // Post-save → idle (back to list). See AssetsPage.handleSave for
        // the rationale; same rule applies uniformly across admin pages.
        if (mode.kind === "create") {
            createMutation.mutate(payload, {
                onSuccess: () => setMode({ kind: "idle" }),
            });
        } else if (mode.kind === "edit") {
            updateMutation.mutate(payload, {
                onSuccess: () => setMode({ kind: "idle" }),
            });
        }
    }

    function handleAddAssetSave(payload: AssetUpsertPayload): void {
        if (mode.kind !== "add-asset") return;
        const workspaceId = mode.workspaceId;
        addAssetMutation.mutate(
            {
                workspaceId,
                payload: {
                    asset: {
                        id: payload.id,
                        sageOfficeId: payload.sageOfficeId,
                        type: payload.type,
                        make: payload.make,
                        model: payload.model,
                        serialNumber: payload.serialNumber,
                        assetTag: payload.assetTag,
                        purchaseDate: payload.purchaseDate,
                        purchasePrice: payload.purchasePrice,
                    },
                    assignmentId: crypto.randomUUID(),
                },
            },
            {
                // Return to the workspace's edit screen — the user is in
                // a campaign loop and the next likely action is "add
                // another asset to the same desk". Idle would force them
                // to re-find the workspace every time.
                onSuccess: () => setMode({ kind: "edit", id: workspaceId }),
            },
        );
    }

    function handlePickedExistingUnassigned(asset: AssetView): void {
        if (assignExistingFor === null) return;
        const workspaceId = assignExistingFor;
        createAssignmentMutation.mutate(
            {
                id: crypto.randomUUID(),
                assetId: asset.id,
                targetKind: "Workspace",
                workspaceId,
            },
            {
                onSuccess: () => setAssignExistingFor(null),
            },
        );
    }

    function handleReassignExistingFromPicker(
        asset: AssetView,
        currentAssignment: AssetAssignmentView,
    ): void {
        if (assignExistingFor === null) return;
        reassignAsset(asset.id, currentAssignment.id, assignExistingFor, () =>
            setAssignExistingFor(null),
        );
    }

    function handleReassignExistingFromAssetForm(existing: AssetView): void {
        if (mode.kind !== "add-asset") return;
        const workspaceId = mode.workspaceId;
        if (
            existing.currentAssignmentId !== null &&
            existing.currentAssignmentId !== undefined &&
            existing.currentAssignmentId !== ""
        ) {
            reassignAsset(existing.id, existing.currentAssignmentId, workspaceId, () =>
                setMode({ kind: "edit", id: workspaceId }),
            );
        } else {
            // No current assignment → just create one. No release needed.
            createAssignmentMutation.mutate(
                {
                    id: crypto.randomUUID(),
                    assetId: existing.id,
                    targetKind: "Workspace",
                    workspaceId,
                },
                {
                    onSuccess: () => setMode({ kind: "edit", id: workspaceId }),
                },
            );
        }
    }

    /**
     * Sequential release-then-create re-assignment. Not a single
     * transactional batch (the release modifies a different doc than the
     * new create on the same partition, which Cosmos *can* batch, but
     * the existing release endpoint isn't shaped that way). Failure
     * mode: if release succeeds and create fails, the asset is
     * unassigned — visible state the user can re-attempt without data
     * corruption.
     */
    function reassignAsset(
        assetId: string,
        currentAssignmentId: string,
        targetWorkspaceId: string,
        onComplete: () => void,
    ): void {
        releaseAssignmentMutation.mutate(
            { assetId, assignmentId: currentAssignmentId },
            {
                onSuccess: () => {
                    createAssignmentMutation.mutate(
                        {
                            id: crypto.randomUUID(),
                            assetId,
                            targetKind: "Workspace",
                            workspaceId: targetWorkspaceId,
                        },
                        { onSuccess: onComplete },
                    );
                },
            },
        );
    }

    function handleStartInventory(workspaceId: string): void {
        navigate(`/admin/workspaces/${encodeURIComponent(workspaceId)}/inventory`);
    }

    async function refreshAfterBulk(): Promise<void> {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: WORKSPACES_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: WORKSPACE_ASSIGNMENTS_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: ASSET_ASSIGNMENTS_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY }),
        ]);
    }

    async function handleBulkAssignOccupant(userId: string): Promise<void> {
        setBulkAssignOpen(false);
        const targets = selectedWorkspaces;
        if (targets.length === 0) {
            setBulkError("Deleted workspaces are skipped. Select active workspaces to assign.");
            return;
        }
        setBulkBusy(true);
        setBulkError(null);
        const failures = await runBulk(
            targets,
            async (workspace) => {
                await createWorkspaceAssignment(client, {
                    id: crypto.randomUUID(),
                    workspaceId: workspace.id,
                    userId,
                });
            },
            workspaceLabel,
        );
        await refreshAfterBulk();
        setBulkBusy(false);
        setBulkError(summarizeBulkFailures(failures));
        if (failures.length === 0) setSelectedKeys(new Set());
    }

    async function handleBulkAvailable(): Promise<void> {
        const targets = selectedWorkspaces.filter((workspace) => {
            const currentId = workspace.currentAssignmentId;
            return currentId !== null && currentId !== undefined && currentId !== "";
        });
        if (targets.length === 0) {
            setBulkIntent(null);
            setBulkError("Those workspaces are already available.");
            return;
        }
        setBulkBusy(true);
        setBulkError(null);
        const failures = await runBulk(
            targets,
            async (workspace) => {
                const currentId = workspace.currentAssignmentId;
                if (currentId === null || currentId === undefined || currentId === "") return;
                await releaseWorkspaceAssignment(client, workspace.id, currentId);
            },
            workspaceLabel,
        );
        await refreshAfterBulk();
        setBulkBusy(false);
        setBulkIntent(null);
        setBulkError(summarizeBulkFailures(failures));
        if (failures.length === 0) setSelectedKeys(new Set());
    }

    async function handleBulkDelete(): Promise<void> {
        const targets = selectedWorkspaces;
        if (targets.length === 0) {
            setBulkIntent(null);
            setBulkError("Deleted workspaces are already removed from the active list.");
            return;
        }
        setBulkBusy(true);
        setBulkError(null);
        const failures = await runBulk(
            targets,
            (workspace) => deleteWorkspace(client, workspace.id),
            workspaceLabel,
        );
        await refreshAfterBulk();
        setBulkBusy(false);
        setBulkIntent(null);
        setBulkError(summarizeBulkFailures(failures));
        if (failures.length === 0) setSelectedKeys(new Set());
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">Workspaces</Text>
                        <Badge tone="neutral">{rows.length}</Badge>
                    </Stack>
                }
                trailing={
                    <Stack direction="row" gap={2} align="center" wrap>
                        <Search size={14} style={{ opacity: 0.55 }} />
                        <div style={{ width: 200 }}>
                            <TextInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Name or number…"
                            />
                        </div>
                        <div style={{ width: 200 }}>
                            <Select
                                value={officeFilter}
                                onChange={(e) => setOfficeFilter(e.target.value)}
                                options={[
                                    { value: "", label: "All offices" },
                                    ...offices.map((o) => ({ value: o.id, label: o.name })),
                                ]}
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <Select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                options={[
                                    { value: "", label: "All types" },
                                    ...workspaceTypes.map((t) => ({ value: t, label: t })),
                                ]}
                            />
                        </div>
                        <Button
                            variant={includeDeleted ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setIncludeDeleted((v) => !v)}
                        >
                            {includeDeleted ? "Showing deleted" : "Show deleted"}
                        </Button>
                        <ExportDataButton
                            filenamePrefix="workspaces"
                            run={async () => {
                                const exported = await exportWorkspaces(client, {
                                    q: search.trim(),
                                    sageOfficeId: officeFilter,
                                    type: typeFilter,
                                    includeDeleted,
                                });
                                return {
                                    csv: buildWorkspacesExportCsv(exported.results),
                                    truncated: exported.truncated,
                                    rowCount: exported.results.length,
                                };
                            }}
                        />
                        {/* Bulk import lives next to "New workspace" because
                            it's the same conceptual action — adding new
                            rows — at a different scale. Hidden on phone:
                            the import flow needs a desktop-class viewport
                            for the preview table, and the operator who
                            uploads CSVs is unlikely to do it from a
                            handset. */}
                        {!isPhone && canEdit ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                leadingIcon={<Upload size={14} />}
                                onClick={() => navigate("/admin/workspaces/bulk-import")}
                            >
                                Bulk import
                            </Button>
                        ) : null}
                        {!isPhone && canEdit ? (
                            <Button
                                variant="primary"
                                size="sm"
                                leadingIcon={<Plus size={16} />}
                                onClick={() => setMode({ kind: "create" })}
                            >
                                New workspace
                            </Button>
                        ) : null}
                    </Stack>
                }
            />

            {canEdit ? (
                <BulkActionBar
                    count={selectedKeys.size}
                    busy={bulkBusy}
                    error={bulkError}
                    onAssign={() => setBulkAssignOpen(true)}
                    onAvailable={() => setBulkIntent("available")}
                    onDelete={() => setBulkIntent("delete")}
                    onClear={() => {
                        setSelectedKeys(new Set());
                        setBulkError(null);
                    }}
                />
            ) : null}

            <SplitPane
                activeBelowDesktop={mode.kind === "idle" ? "master" : "detail"}
                master={
                    <WorkspacesList
                        rows={rows}
                        officeIndex={officeIndex}
                        selectedId={
                            mode.kind === "edit" || mode.kind === "add-asset"
                                ? mode.kind === "edit"
                                    ? mode.id
                                    : mode.workspaceId
                                : null
                        }
                        onSelect={(row) => setMode({ kind: "edit", id: row.id })}
                        onStartInventory={handleStartInventory}
                        isLoading={workspacesQuery.isPending}
                        error={
                            workspacesQuery.isError ? describeApiError(workspacesQuery.error) : null
                        }
                        {...(canEdit
                            ? { selection: { selectedKeys, onChange: setSelectedKeys } }
                            : {})}
                    />
                }
                detail={renderDetail()}
            />

            {isPhone && canEdit && mode.kind === "idle" ? (
                <BottomActionBar>
                    <Button
                        variant="primary"
                        leadingIcon={<Plus size={16} />}
                        onClick={() => setMode({ kind: "create" })}
                    >
                        New workspace
                    </Button>
                </BottomActionBar>
            ) : null}

            <AssignTargetDialog
                open={bulkAssignOpen}
                mode={{ kind: "assign" }}
                defaultKind="User"
                lockedKind="User"
                assetLabel={
                    selectedWorkspaces.length === 1
                        ? "1 workspace"
                        : `${selectedWorkspaces.length} workspaces`
                }
                onSubmit={(target) => {
                    if (target.kind !== "User") return;
                    void handleBulkAssignOccupant(target.id);
                }}
                onCancel={() => setBulkAssignOpen(false)}
                isWorking={bulkBusy}
                errorMessage={null}
            />
            <ConfirmAction
                open={bulkIntent === "available"}
                title="Make available?"
                description="Releases the occupant on each selected workspace so the desk is available. Workspaces that are already vacant are left as they are."
                confirmLabel="Available"
                busy={bulkBusy}
                onCancel={() => setBulkIntent(null)}
                onConfirm={() => {
                    void handleBulkAvailable();
                }}
            />
            <ConfirmAction
                open={bulkIntent === "delete"}
                title="Delete selected workspaces?"
                description="Marks each selected workspace deleted. A workspace that still has an occupant or open assets has to be made available first."
                confirmLabel="Delete"
                destructive
                busy={bulkBusy}
                onCancel={() => setBulkIntent(null)}
                onConfirm={() => {
                    void handleBulkDelete();
                }}
            />
            <ConfirmAction
                open={pendingDelete !== null}
                title="Delete workspace?"
                description="The workspace will be marked deleted. Any open asset assignments to it must be released first."
                confirmLabel="Delete"
                destructive
                busy={deleteMutation.isPending}
                onCancel={() => setPendingDelete(null)}
                onConfirm={() => {
                    if (pendingDelete === null) return;
                    const id = pendingDelete;
                    deleteMutation.mutate(id, {
                        onSuccess: () => {
                            setPendingDelete(null);
                            setMode({ kind: "idle" });
                        },
                    });
                }}
            />
            <ConfirmAction
                open={pendingRestore !== null}
                title="Restore workspace?"
                description="The workspace will become active again. Its sage office must also be active."
                confirmLabel="Restore"
                busy={restoreMutation.isPending}
                onCancel={() => setPendingRestore(null)}
                onConfirm={() => {
                    if (pendingRestore === null) return;
                    restoreMutation.mutate(pendingRestore, {
                        onSuccess: () => {
                            setPendingRestore(null);
                            setMode({ kind: "idle" });
                        },
                    });
                }}
            />
            <ConfirmAction
                open={pendingRelease !== null}
                title="Release asset from this workspace?"
                description="The asset will become unassigned. You can reassign it later from the assets or assignments page."
                confirmLabel="Release"
                busy={releaseAssignmentMutation.isPending}
                onCancel={() => setPendingRelease(null)}
                onConfirm={() => {
                    if (pendingRelease === null) return;
                    const target = pendingRelease;
                    releaseAssignmentMutation.mutate(
                        { assetId: target.assetId, assignmentId: target.id },
                        { onSuccess: () => setPendingRelease(null) },
                    );
                }}
            />

            {targetWorkspace !== null && assignExistingFor !== null ? (
                <AssetPickerDialog
                    open
                    targetWorkspace={targetWorkspace}
                    onAssignUnassigned={handlePickedExistingUnassigned}
                    onReassignAsset={handleReassignExistingFromPicker}
                    onCancel={() => setAssignExistingFor(null)}
                    isWorking={
                        createAssignmentMutation.isPending || releaseAssignmentMutation.isPending
                    }
                />
            ) : null}

            {reassignAllSource !== null ? (
                <ReassignAllAssetsDialog
                    sourceWorkspace={reassignAllSource}
                    onClose={() => setReassignAllFor(null)}
                />
            ) : null}
        </Stack>
    );

    function renderDetail(): ReactElement {
        if (mode.kind === "add-asset") {
            return (
                <AssetForm
                    mode={{ kind: "create" }}
                    {...(assetFormWorkspaceContext !== undefined
                        ? { workspaceContext: assetFormWorkspaceContext }
                        : {})}
                    onSave={handleAddAssetSave}
                    onCancel={() => setMode({ kind: "edit", id: mode.workspaceId })}
                    onRequestDelete={() => undefined}
                    onRequestRestore={() => undefined}
                    onReassignExisting={handleReassignExistingFromAssetForm}
                    isSaving={isAssetSaving}
                    errorMessage={assetFormError}
                />
            );
        }

        if (formMode === null) {
            return (
                <EmptyState
                    icon={<LayoutGrid size={32} />}
                    title={canEdit ? "Select a workspace to edit" : "Select a workspace to view"}
                    description={
                        canEdit
                            ? "Pick a row on the left, or create a new workspace."
                            : "Pick a row on the left."
                    }
                    action={
                        canEdit ? (
                            <Button
                                variant="primary"
                                leadingIcon={<Plus size={16} />}
                                onClick={() => setMode({ kind: "create" })}
                            >
                                New workspace
                            </Button>
                        ) : undefined
                    }
                />
            );
        }

        // Viewer-side: still attach all callbacks so the form renders its
        // sections; the form itself reads useCanEdit() and disables/hides
        // the controls. Mutation callbacks remain wired because Save/Delete
        // buttons are hidden — they can't fire.
        return (
            <WorkspaceForm
                mode={formMode}
                offices={offices}
                onSave={handleSave}
                onCancel={() => setMode({ kind: "idle" })}
                onRequestDelete={(id) => setPendingDelete(id)}
                onRequestRestore={(id) => setPendingRestore(id)}
                onRequestAddAsset={(id) => setMode({ kind: "add-asset", workspaceId: id })}
                onRequestAssignExisting={(id) => setAssignExistingFor(id)}
                onRequestReleaseAssignment={(assignment) => setPendingRelease(assignment)}
                onRequestStartInventory={handleStartInventory}
                onRequestReassignAll={(id) => setReassignAllFor(id)}
                onRequestAssignOccupant={handleAssignOccupant}
                onRequestReleaseOccupant={handleReleaseOccupant}
                isOccupantBusy={isOccupantBusy}
                occupantErrorMessage={occupantErrorMessage}
                isSaving={isSaving}
                errorMessage={formError}
            />
        );
    }
}

function workspaceLabel(workspace: WorkspaceView): string {
    return workspace.name;
}
